import React, { useState, useEffect } from 'react'
import { WindowType, ShapeType } from '../services/windowsApi'
import { DoorTypeEnum } from '../services/doorsApi'
import {
    Box,
    Typography,
    Grid,
    Card,
    CardActionArea,
    Button,
    useTheme,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material'
import {
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@mui/icons-material'
import { useLanguage } from '../../Commun/contexts/LanguageContext'
import { templatesApi } from '../services/templatesApi'

export interface Template {
    id: string
    name: string
    type: 'window' | 'door'
    windowType?: WindowType
    doorType?: DoorTypeEnum
    shapeType: ShapeType
    icon: string
    description: string
    defaultWidth: number
    defaultHeight: number
}

interface TemplateLibraryProps {
    onSelectTemplate: (template: Template) => void
    designMode: 'window' | 'door'
    onEditTemplate?: (template: Template) => void
    hideActions?: boolean
}

const TemplateLibrary: React.FC<TemplateLibraryProps> = ({ onSelectTemplate, designMode, onEditTemplate, hideActions = false }) => {
    const [templates, setTemplates] = useState<Template[]>([])
    const [loading, setLoading] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [templateToDelete, setTemplateToDelete] = useState<string | null>(null)

    const theme = useTheme();
    const { t } = useLanguage();
    const dataFetchedRef = React.useRef(false);

    const fetchTemplates = async () => {
        setLoading(true)
        // Fetch ALL templates once
        const result = await templatesApi.getAll()
        if (result.success) {
            setTemplates(result.data)
        }
        setLoading(false)
    }

    useEffect(() => {
        if (dataFetchedRef.current) return;
        dataFetchedRef.current = true;
        fetchTemplates()
    }, [])

    // Exposed refresh method for parent if needed, or rely on parent passing a refresh trigger prop
    // ideally we would expose a ref or context, but for now we can just rely on parent forcing remount or passing a prop.
    // Actually, simplest is to just expose a prop `refreshTrigger` if needed, 
    // but the user requirement "send one request" implies we load once.
    // If we create a NEW template in parent, we might need to refresh this list. 
    // Let's add a generic listener or just re-fetch if we assume parent manages it?
    // Wait, if parent creates, this component won't know unless we lift state up OR expose a refresh mechanism.
    // For now, I will keep fetch internal but maybe add a dependency on a prop if I add one later.

    const handleDeleteClick = (id: string, e: React.MouseEvent) => {
        e.stopPropagation()
        setTemplateToDelete(id)
        setDeleteDialogOpen(true)
    }

    const confirmDelete = async () => {
        if (templateToDelete) {
            await templatesApi.delete(templateToDelete)
            // We need to re-fetch all to keep state consistent
            fetchTemplates()
            setDeleteDialogOpen(false)
            setTemplateToDelete(null)
        }
    }

    const handleEdit = (template: Template, e: React.MouseEvent) => {
        e.stopPropagation()
        if (onEditTemplate) {
            onEditTemplate(template)
        }
    }

    // Filter locally
    const filteredTemplates = templates.filter(t => t.type === designMode)


    return (
        <Box sx={{ p: 1 }}>
            <Grid container spacing={2}>
                {filteredTemplates.map((template) => (
                    <Grid item xs={12} sm={6} md={4} key={template.id}>
                        <Card
                            variant="outlined"
                            sx={{
                                height: '100%',
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.2s',
                                bgcolor: 'background.paper',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    transform: 'translateY(-2px)',
                                    boxShadow: theme.shadows[4]
                                },
                                '&:hover .actions': { opacity: 1 }
                            }}
                        >
                            <CardActionArea
                                onClick={() => onSelectTemplate(template)}
                                sx={{ height: '100%', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' }}
                            >
                                <Box
                                    sx={{
                                        minWidth: 60,
                                        height: 60,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'action.hover',
                                        borderRadius: 2,
                                        mb: 2,
                                        px: 1,
                                        whiteSpace: 'nowrap',
                                        fontSize: '2.5rem'
                                    }}
                                >
                                    {template.icon}
                                </Box>
                                <Typography variant="h6" component="div" fontWeight="bold" gutterBottom>
                                    {template.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                                    {template.description}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        bgcolor: 'action.selected',
                                        color: 'text.secondary',
                                        fontWeight: 'medium',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        border: 1,
                                        borderColor: 'divider'
                                    }}
                                >
                                    {template.defaultWidth}mm × {template.defaultHeight}mm
                                </Typography>
                            </CardActionArea>
                            {!hideActions && (
                                <Box className="actions" sx={{ position: 'absolute', top: 8, right: 8, opacity: 0, transition: 'opacity 0.2s' }}>
                                    <IconButton size="small" onClick={(e) => handleEdit(template, e)}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" color="error" onClick={(e) => handleDeleteClick(template.id, e)}><DeleteIcon fontSize="small" /></IconButton>
                                </Box>
                            )}
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{t('common.confirm.title.delete')}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t('common.confirm.delete')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>{t('btn.cancel')}</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained" autoFocus>{t('common.actions.confirm')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default TemplateLibrary
